import i18n from '../i18n';

/**
 * Gets a user-friendly error message based on HTTP status code
 * @param status - HTTP status code
 * @returns User-friendly error message
 */
const getTranslatedStatusErrorMessage = (status: number): string => {
  switch (status) {
    case 400:
      return i18n.t('error.invalid_request');
    case 401:
      return i18n.t('error.session_expired');
    case 403:
      return i18n.t('error.permission_denied');
    case 404:
      return i18n.t('error.resource_not_found');
    case 408:
      return i18n.t('error.request_timeout');
    case 409:
      return i18n.t('error.conflict');
    case 422:
      return i18n.t('error.validation_error');
    case 429:
      return i18n.t('error.too_many_requests');
    case 500:
      return i18n.t('error.server_error');
    case 502:
      return i18n.t('error.bad_gateway');
    case 503:
      return i18n.t('error.service_unavailable');
    case 504:
      return i18n.t('error.gateway_timeout');
    default:
      if (status >= 400 && status < 500) {
        return i18n.t('error.client_error');
      } else if (status >= 500) {
        return i18n.t('error.server_error_generic');
      }
      return i18n.t('error.unexpected_error');
  }
};

/**
 * Extracts a human-readable error message from various error types.
 * Handles Axios errors, standard Error objects, and unknown error types.
 *
 * @param error - The error to extract a message from
 * @param defaultMessage
 * @returns
 */
export const extractErrorMessage = (
  error: unknown,
  defaultMessage?: string
): string => {
  // Handle Axios errors (errors with response property)
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: {
        status?: number;
        data?: { message?: string; error?: string };
      };
      request?: unknown;
      message?: string;
      code?: string;
    };

    // Try to get message from response data first
    if (axiosError.response?.data) {
      const apiMessage =
        axiosError.response.data.message || axiosError.response.data.error;
      if (apiMessage) {
        return apiMessage;
      }
    }

    // If no message in response, use status code message
    if (axiosError.response?.status) {
      return getTranslatedStatusErrorMessage(axiosError.response.status);
    }

    // Handle network errors (no response)
    if (axiosError.request && !axiosError.response) {
      if (axiosError.code === 'ECONNABORTED') {
        return i18n.t('error.connection_timeout');
      }
      if (axiosError.message === 'Network Error') {
        return i18n.t('error.network_error');
      }
      return i18n.t('error.connection_failed');
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Return default message for unknown error types
  return defaultMessage
    ? i18n.t(defaultMessage, { defaultValue: defaultMessage })
    : i18n.t('error.unknown_error');
};
